import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { BiSearch, BiPlus, BiEdit, BiShow, BiSpreadsheet, BiPrinter } from "react-icons/bi";
import Select from "react-select";

import { getQoList, cekQoApprover, quotationV2Selector } from "./quotationV2Slice";
import { Colors } from "../../../assets/Colors";

// ── Status config ──────────────────────────────────────────────────
const STATUS_CONFIG = {
  draft:     { label: "Draft",      bg: "bg-gray-100",   text: "text-gray-600",   dot: "bg-gray-400"   },
  ready:     { label: "Siap JR",    bg: "bg-green-100",  text: "text-green-700",  dot: "bg-green-500"  },
  berjalan:  { label: "Berjalan",   bg: "bg-blue-100",   text: "text-blue-700",   dot: "bg-blue-500"   },
  expired:   { label: "Kadaluarsa", bg: "bg-gray-200",   text: "text-gray-500",   dot: "bg-gray-400"   },
  // status lama (data legacy, jika ada)
  submitted: { label: "Menunggu Approval", bg: "bg-yellow-100", text: "text-yellow-700", dot: "bg-yellow-500" },
  approved:  { label: "Disetujui",  bg: "bg-green-100",  text: "text-green-700",  dot: "bg-green-500"  },
  rejected:  { label: "Ditolak",    bg: "bg-red-100",    text: "text-red-700",    dot: "bg-red-500"    },
};

const TABS = [
  { key: "",         label: "Semua" },
  { key: "ready",    label: "Siap JR" },
  { key: "berjalan", label: "Berjalan" },
  { key: "draft",    label: "Draft" },
  { key: "expired",  label: "Kadaluarsa" },
];

// Status efektif: QO "Siap JR" yang sudah punya JR ber-Activity dianggap "Berjalan".
function effStatus(row) {
  if (row.Status === "ready" && (row.JrActiveCount ?? 0) > 0) return "berjalan";
  return row.Status;
}

// ── Small components ───────────────────────────────────────────────
function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.draft;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${cfg.bg} ${cfg.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

function ActivityChips({ choice }) {
  if (!choice) return <span className="text-gray-400 text-xs">—</span>;
  const items = choice.split(",").map(s => s.trim()).filter(Boolean);
  const show  = items.slice(0, 3);
  const rest  = items.length - show.length;
  return (
    <div className="flex flex-wrap gap-1">
      {show.map(a => (
        <span key={a} className="px-1.5 py-0.5 rounded text-xs font-medium bg-orange-50 text-orange-700 border border-orange-200">
          {a.replace(/_/g, " ")}
        </span>
      ))}
      {rest > 0 && (
        <span className="px-1.5 py-0.5 rounded text-xs text-gray-500 bg-gray-100">+{rest}</span>
      )}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────
export default function QuotationMonitoringPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const qoV2     = useSelector(quotationV2Selector);

  const [search,     setSearch]     = useState("");
  const [activeTab,  setActiveTab]  = useState("");
  const [inputValue, setInputValue] = useState("");
  const [klienFilter, setKlienFilter] = useState(""); // KlienID, "" = semua

  // ── Load list ──
  const load = useCallback((q = search, status = activeTab) => {
    dispatch(getQoList({ search: q, QoType: "1" }));
  }, [dispatch, search, activeTab]);

  useEffect(() => { load("", ""); }, []); // eslint-disable-line
  useEffect(() => { dispatch(cekQoApprover()); }, [dispatch]);

  // Pemberi approval Quotation tidak diberi jalan mengedit QO maupun membuat JR
  // dari halaman ini — sama seperti di halaman detail.
  const isApprover = qoV2.canApproveQo === true;
  // Bagian FM-IT/BD tetap boleh mengedit QO yang sudah disetujui (dicek ulang di backend).
  const canEditApproved = qoV2.canEditApprovedQo === true;

  // ── Search: debounce 400ms ──
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(inputValue);
      dispatch(getQoList({ search: inputValue, QoType: "1" }));
    }, 400);
    return () => clearTimeout(t);
  }, [inputValue]); // eslint-disable-line

  // ── Pilihan klien: dari daftar QO yang dimuat. Nama klien bisa kembar
  //    (mis. dua "PT PERTAMINA HULU KALIMANTAN TIMUR"), jadi KlienID ikut ditampilkan.
  const klienOptions = useMemo(() => {
    const m = new Map();
    (qoV2.list ?? []).forEach(r => {
      if (r.KlienID && !m.has(r.KlienID)) m.set(r.KlienID, r.NamaKlien || r.KlienID);
    });
    return [...m].map(([value, nama]) => ({ value, label: `${nama} (${value})` }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [qoV2.list]);

  // Filter klien berlaku sebelum tab status, agar jumlah di tiap tab ikut menyempit.
  const byKlien = (qoV2.list ?? []).filter(r => !klienFilter || r.KlienID === klienFilter);

  // ── Filter by status (efektif) client-side ──
  const filtered = byKlien.filter(row =>
    !activeTab || effStatus(row) === activeTab
  );

  // ── Count per status (efektif) for tab badges ──
  const countByStatus = byKlien.reduce((acc, r) => {
    const s = effStatus(r);
    acc[s] = (acc[s] ?? 0) + 1;
    return acc;
  }, {});

  const handleRowClick = (row) => {
    // Approver selalu diarahkan ke detail: baris draft yang dulu langsung membuka
    // form edit adalah pintu belakang menuju pengubahan isi QO.
    if (row.Status === "draft" && !isApprover) {
      navigate("/edit-quotation-or-contract", { state: { QoNo: row.QoNo } });
    } else {
      navigate("/quotation-detail-or-contract", { state: { QoNo: row.QoNo } });
    }
  };

  // ── Render ──
  return (
    <div className="md:ml-64">

      {/* ── Top bar (orange) ── */}
      <div style={{ background: Colors.primary }} className="pt-14 pb-20 px-8 h-auto" />

      {/* ── Content card ── */}
      <div className="px-4 md:px-8 -mt-16">
        <div className="bg-white rounded-xl shadow-md overflow-hidden">

          {/* ── Header ── */}
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <h2 className="text-lg font-bold text-gray-800">Daftar Quotation / Kontrak</h2>
            <button
              onClick={() => navigate("/add-quotation-or-contract")}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-white text-sm font-semibold"
              style={{ background: Colors.primary }}
            >
              <BiPlus className="text-lg" />
              Buat QO Baru
            </button>
          </div>

          {/* ── Search ── */}
          <div className="flex flex-wrap items-center gap-3 px-6 py-3 border-b bg-gray-50">
            <div className="relative w-full max-w-sm">
              <BiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
              <input
                type="text"
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                placeholder="Cari no. QO, no. kontrak, atau nama klien…"
                className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg outline-none focus:ring-2 focus:border-transparent"
                style={{ "--tw-ring-color": Colors.primary + "40" }}
              />
            </div>
            <div className="w-full sm:w-72 text-sm">
              <Select
                menuPortalTarget={document.body}
                styles={{ menuPortal: b => ({ ...b, zIndex: 9999 }) }}
                isClearable
                value={klienOptions.filter(o => o.value === klienFilter)}
                onChange={o => setKlienFilter(o?.value ?? "")}
                options={klienOptions}
                placeholder="Semua Klien"
                noOptionsMessage={() => "Klien tidak ditemukan"}
              />
            </div>
          </div>

          {/* ── Tabs ── */}
          <div className="flex gap-0 border-b overflow-x-auto">
            {TABS.map(tab => {
              const count = tab.key
                ? (countByStatus[tab.key] ?? 0)
                : byKlien.length;
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex items-center gap-1.5 px-5 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                    isActive
                      ? "border-orange-500 text-orange-600"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {tab.label}
                  {count > 0 && (
                    <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                      isActive ? "bg-orange-100 text-orange-700" : "bg-gray-100 text-gray-500"
                    }`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* ── Table ── */}
          <div className="overflow-x-auto">
            {qoV2.isListLoading ? (
              <div className="flex items-center justify-center py-16 text-gray-400 text-sm">
                Memuat data…
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                <span className="text-4xl mb-3">📋</span>
                <p className="text-sm">Tidak ada data quotation</p>
                {(activeTab || klienFilter) && (
                  <button
                    onClick={() => { setActiveTab(""); setKlienFilter(""); }}
                    className="mt-2 text-xs text-orange-500 underline"
                  >
                    Tampilkan semua
                  </button>
                )}
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th className="px-5 py-3 font-semibold text-gray-600 whitespace-nowrap">No. QO</th>
                    <th className="px-5 py-3 font-semibold text-gray-600 whitespace-nowrap">No. Kontrak</th>
                    <th className="px-5 py-3 font-semibold text-gray-600 whitespace-nowrap">Nama Klien</th>
                    <th className="px-5 py-3 font-semibold text-gray-600 whitespace-nowrap">Periode Kontrak</th>
                    <th className="px-5 py-3 font-semibold text-gray-600 whitespace-nowrap">Aktivitas</th>
                    <th className="px-5 py-3 font-semibold text-gray-600 whitespace-nowrap">Status</th>
                    <th className="px-5 py-3 font-semibold text-gray-600 whitespace-nowrap text-right">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map(row => (
                    <tr
                      key={row.QoID}
                      className="hover:bg-orange-50 cursor-pointer transition-colors"
                      onClick={() => handleRowClick(row)}
                    >
                      <td className="px-5 py-3.5 font-mono text-xs text-gray-700 whitespace-nowrap">
                        {row.QoNo}
                      </td>
                      <td className="px-5 py-3.5 text-gray-700 max-w-[200px] truncate" title={row.NoKontrak}>
                        {row.NoKontrak || <span className="text-gray-400">—</span>}
                      </td>
                      <td className="px-5 py-3.5 text-gray-700 max-w-[180px] truncate" title={row.NamaKlien}>
                        {row.NamaKlien || row.KlienID}
                      </td>
                      <td className="px-5 py-3.5 whitespace-nowrap">
                        <div className="text-gray-700">
                          {row.StartDate} <span className="text-gray-400 mx-1">→</span> {row.ExpireDate}
                        </div>
                      </td>
                      <td className="px-5 py-3.5">
                        <ActivityChips choice={row.ActivityChoice} />
                      </td>
                      <td className="px-5 py-3.5 whitespace-nowrap">
                        <StatusBadge status={effStatus(row)} />
                        {row.JrCount > 0 && (
                          <span className="ml-1.5 text-[11px] text-gray-400">{row.JrCount} JR</span>
                        )}
                      </td>
                      <td className="px-5 py-3.5 text-right" onClick={e => e.stopPropagation()}>
                        <div className="inline-flex items-center gap-2">
                          {row.Status === "draft" ? (
                            <>
                              {!isApprover && (
                                <button
                                  title="Edit"
                                  onClick={() => navigate("/edit-quotation-or-contract", { state: { QoNo: row.QoNo } })}
                                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border text-xs font-medium text-orange-600 border-orange-300 hover:bg-orange-50"
                                >
                                  <BiEdit className="text-sm" /> Edit
                                </button>
                              )}
                              {/* Draft dulu HANYA punya tombol Edit, dan klik barisnya pun
                                  langsung ke form (handleRowClick). Akibatnya halaman detail
                                  tak punya pintu masuk untuk draft — padahal di sanalah tombol
                                  "Kirim untuk Approval" berada, sehingga QO draft tak bisa
                                  dinaikkan statusnya sama sekali. */}
                              <button
                                title="Lihat detail & kirim untuk approval"
                                onClick={() => navigate("/quotation-detail-or-contract", { state: { QoNo: row.QoNo } })}
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border text-xs font-medium text-gray-600 border-gray-300 hover:bg-gray-50"
                              >
                                <BiShow className="text-sm" /> Detail
                              </button>
                            </>
                          ) : (
                            <>
                              {row.Status === "approved" && canEditApproved && (
                                <button
                                  title="Edit quotation yang sudah disetujui"
                                  onClick={() => navigate("/edit-quotation-or-contract", { state: { QoNo: row.QoNo } })}
                                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border text-xs font-medium text-orange-600 border-orange-300 hover:bg-orange-50"
                                >
                                  <BiEdit className="text-sm" /> Edit
                                </button>
                              )}
                              <button
                                title="Lihat detail"
                                onClick={() => navigate("/quotation-detail-or-contract", { state: { QoNo: row.QoNo } })}
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border text-xs font-medium text-gray-600 border-gray-300 hover:bg-gray-50"
                              >
                                <BiShow className="text-sm" /> Detail
                              </button>
                              <button
                                title="Lihat MJS per Quotation"
                                onClick={() => navigate("/mjs", { state: {
                                  QuatationNo: row.QoNo,
                                  isV2: true,
                                  header: {
                                    NoKontrak: row.NoKontrak,
                                    NamaKlien: row.NamaKlien,
                                    KlienID: row.KlienID,
                                    ExpireDate: row.ExpireDate,
                                    WorkDesc: row.WorkDesc,
                                  },
                                } })}
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border text-xs font-medium text-blue-600 border-blue-300 hover:bg-blue-50"
                              >
                                <BiSpreadsheet className="text-sm" /> MJS
                              </button>
                              <button
                                title="Buat invoice dari JR approved pada quotation ini"
                                onClick={() => navigate("/invoice-generate-v2", { state: {
                                  KlienID: row.KlienID,
                                  QoNo: row.QoNo,
                                } })}
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border text-xs font-medium text-green-700 border-green-300 hover:bg-green-50"
                              >
                                <BiPrinter className="text-sm" /> Cetak Invoice
                              </button>
                              {/* Amandemen — induknya ditentukan oleh BARIS YANG DIKLIK, tidak
                                  pernah diketik. Itulah sebabnya tombolnya di sini dan bukan di
                                  form: dulu induk diisi manual dan bisa menunjuk QO yang tak ada
                                  (00008/AQ & 00010/AQ menunjuk 00007 yang tidak pernah ada).
                                  Hanya untuk yang approved: draft cukup diedit, yang ditolak tak
                                  punya apa pun untuk diamandemen. Yang sudah lewat masa berlaku
                                  TETAP boleh — justru itu kasus perpanjangan yang paling sering. */}
                              {row.Status === "approved" && !isApprover && (
                                <button
                                  title="Buat Additional Quotation yang menginduk ke quotation ini"
                                  onClick={() => navigate("/add-quotation-or-contract", { state: {
                                    ParentQoNo: row.QoNo,
                                    ParentKlienID: row.KlienID,
                                    ParentNamaKlien: row.NamaKlien,
                                    ParentNoKontrak: row.NoKontrak,
                                    ParentStartDate: row.StartDate,
                                    ParentExpireDate: row.ExpireDate,
                                  } })}
                                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border text-xs font-medium text-purple-700 border-purple-300 hover:bg-purple-50"
                                >
                                  <BiPlus className="text-sm" /> Amandemen
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* ── Footer summary ── */}
          {!qoV2.isListLoading && filtered.length > 0 && (
            <div className="px-6 py-3 border-t bg-gray-50 text-xs text-gray-500">
              Menampilkan <strong>{filtered.length}</strong> dari <strong>{(qoV2.list ?? []).length}</strong> quotation
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
